import React, { useState } from 'react';
import { FaTimes, FaSpinner, FaPalette, FaCode } from 'react-icons/fa';
import api from '../../../services/api';
import EmailTemplateEditor from './EmailTemplateEditor';

const DEFAULT_HTML = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; }
    .container { max-width: 700px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .header { background: #1e40af; color: white; padding: 24px; }
    .header h1 { margin: 0; font-size: 20px; }
    .content { padding: 24px; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
    th { background: #f3f4f6; padding: 10px 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #6b7280; border-bottom: 2px solid #e5e7eb; }
    td { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #374151; }
    tr:hover { background: #f9fafb; }
    .footer { padding: 16px 24px; background: #f9fafb; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af; text-align: center; }
    .meta { font-size: 13px; color: #6b7280; margin-bottom: 16px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>{{meta.reportName}}</h1>
    </div>
    <div class="content">
      <p class="meta">Generated: {{meta.generatedAt}} | Rows: {{meta.rowCount}}</p>
      <table>
        <thead>
          <tr>
            <th>Column 1</th>
            <th>Column 2</th>
          </tr>
        </thead>
        <tbody>
          {{#each rows}}
          <tr>
            <td>{{this.Column1}}</td>
            <td>{{this.Column2}}</td>
          </tr>
          {{/each}}
        </tbody>
      </table>
    </div>
    <div class="footer">
      This report was generated automatically by Operations Dashboard.
    </div>
  </div>
</body>
</html>`;

const CreateEmailTemplateModal = ({ isOpen, onClose, onCreated }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    templateName: '',
    description: '',
    templateType: 'advanced_report',
    subjectTemplate: '{{meta.reportName}} - {{meta.generatedAt}}',
    htmlTemplate: DEFAULT_HTML,
    isDefault: false,
  });

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
      setError(null);
      setFormData({
        templateName: '',
        description: '',
        templateType: 'advanced_report',
        subjectTemplate: '{{meta.reportName}} - {{meta.generatedAt}}',
        htmlTemplate: DEFAULT_HTML,
        isDefault: false,
      });
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!formData.templateName.trim()) {
      setError('Template name is required');
      return;
    }
    if (!formData.htmlTemplate.trim()) {
      setError('HTML template content is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.emailReports.createTemplate({
        templateName: formData.templateName.trim(),
        description: formData.description.trim(),
        templateType: formData.templateType,
        subjectTemplate: formData.subjectTemplate,
        htmlTemplate: formData.htmlTemplate,
        isDefault: formData.isDefault,
      });
      onCreated(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-surface rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
              <FaPalette />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Create Email Template</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-5">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('subject')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'subject'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Subject Line
          </button>
          <button
            onClick={() => setActiveTab('template')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'template'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FaCode className="inline mr-1.5" />
            HTML Template
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.templateName}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, templateName: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Weekly Summary Report"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows="3"
                  placeholder="Optional description of the template"
                />
              </div>
            </div>
          )}

          {activeTab === 'subject' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject Line Template
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Use Handlebars expressions for dynamic values. Available: {'{{meta.reportName}}'},{' '}
                  {'{{meta.generatedAt}}'}, {'{{meta.rowCount}}'}
                </p>
                <input
                  type="text"
                  value={formData.subjectTemplate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, subjectTemplate: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  placeholder="{{meta.reportName}} - Report"
                />
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
                <p className="text-sm text-gray-600">
                  {formData.subjectTemplate
                    .replace('{{meta.reportName}}', 'My Report')
                    .replace('{{meta.generatedAt}}', new Date().toLocaleDateString())
                    .replace('{{meta.rowCount}}', '42')}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'template' && (
            <EmailTemplateEditor
              value={formData.htmlTemplate}
              onChange={(val) => setFormData((prev) => ({ ...prev, htmlTemplate: val }))}
              label="HTML Email Template"
              minHeight={400}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-5 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-surface border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
            disabled={loading}
          >
            {loading && <FaSpinner className="animate-spin" />}
            <span>{loading ? 'Creating...' : 'Create Template'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEmailTemplateModal;
